import { AnyObject, Id, Maybe } from '@vimeo/core/types/generic';
import {
  PostLiveEventActivateFields,
  PostLiveEventActivateResult,
} from '@vimeo/gctl-api/fetchers/live_event/activate';
import {
  PostLiveEventEndFields,
  PostLiveEventEndResult,
} from '@vimeo/gctl-api/fetchers/live_event/end';

import { TConnectionSupportedMethodParams, withConnectionSupport } from './base';
import { ERequestMethod } from '../types/api';
import { ISessionInfo } from '../types/event';

/**
 * Activate live event instance for specified RLE.
 */
export function activateEvent(
  params: TConnectionSupportedMethodParams<{ liveEventId: Id }>,
): Promise<PostLiveEventActivateResult<PostLiveEventActivateFields>> {
  return withConnectionSupport(ERequestMethod.POST, params, ({ liveEventId }) => ({
    path: `/live_events/${liveEventId}/activate`,
  }));
}

/**
 * Start + activate live event instance for specified event id.
 */
export function startEvent(
  params: TConnectionSupportedMethodParams<{ liveEventId: Id }>,
): Promise<PostLiveEventActivateResult<PostLiveEventActivateFields>> {
  return withConnectionSupport(
    ERequestMethod.POST,
    params,
    ({ liveEventId }) => ({
      path: `/live_events/${liveEventId}/activate`,
    }),
    { body: { streamingStartRequested: true } },
  );
}

/**
 * Activate recurring live event instance for specified RLE.
 */
export function endEvent(
  params: TConnectionSupportedMethodParams<{ liveEventId: Id }>,
): Promise<PostLiveEventEndResult<PostLiveEventEndFields>> {
  return withConnectionSupport(ERequestMethod.POST, params, ({ liveEventId }) => ({
    path: `/live_events/${liveEventId}/end`,
  }));
}

export interface IGetSessionIngestStatusConfig {
  // Just used for cache busting to avoid wrong cache reading if request is same for few broadcaster tabs.
  cacheKey?: Maybe<Id>;
  cloudSessionId?: Maybe<Id>;
  isRawStatus?: boolean;
}

/**
 * Get active session ingest status and info.
 *
 * @param params - standard HTTP parameters object to configure the request
 * @param config - configuration of request from perspective of status polling
 * @returns response containing session status information
 */
export function getSessionIngestStatus(
  params: TConnectionSupportedMethodParams<{ clipId: Id }>,
  config: IGetSessionIngestStatusConfig = {},
): Promise<ISessionInfo> {
  const query: AnyObject = (params as any)['query'] ?? {};

  if (config.cloudSessionId) {
    query.session_id = config.cloudSessionId;
  }

  if (config.isRawStatus) {
    query.get_raw_ingest_status = true;
  }

  if (config.cacheKey) {
    query.cache_key = config.cacheKey;
  }

  return withConnectionSupport(
    ERequestMethod.GET,
    params,
    ({ clipId }) => ({
      path: `/videos/${clipId}/sessions/status`,
    }),
    { query },
  );
}
